import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import GallerySlider from '../components/GallerySlider';

const Home = () => {
  useEffect(() => {
    document.title = "Home - Gujarat State APP Association 2025";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = "Welcome to the Gujarat State Assistant Public Prosecutors Association (GSAPPPA) 2025. Discover our mission, events, and connect with Assistant Public Prosecutors in Gujarat. Join the leading Government Prosecutors Association for legal excellence and Criminal Law Prosecutors Gujarat.";
    }
    
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.href = "https://gsappa.kevalontechnology.in/";
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = 'https://gsappa.kevalontechnology.in/';
      document.head.appendChild(canonical);
    }
    
    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.content = "Home - Gujarat State APP Association 2025";
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.content = "Welcome to the Gujarat State Assistant Public Prosecutors Association (GSAPPPA) 2025. Discover our mission, events, and connect with Assistant Public Prosecutors in Gujarat. Join the leading Government Prosecutors Association for legal excellence and Criminal Law Prosecutors Gujarat.";
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.content = "https://gsappa.kevalontechnology.in/";
    
    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) twitterTitle.content = "Home - Gujarat State APP Association 2025";
    
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) twitterDescription.content = "Welcome to the Gujarat State Assistant Public Prosecutors Association (GSAPPPA) 2025. Discover our mission, events, and connect with Assistant Public Prosecutors in Gujarat. Join the leading Government Prosecutors Association for legal excellence and Criminal Law Prosecutors Gujarat.";
    
    const twitterUrl = document.querySelector('meta[property="twitter:url"]');
    if (twitterUrl) twitterUrl.content = "https://gsappa.kevalontechnology.in/";
  }, []);
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const heroVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-light-grey">
      {/* Hero Section */}
      <motion.section 
        className="bg-gradient-to-r from-deep-black via-deep-black to-gold text-white py-24 relative overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={heroVariants}
      >
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.h1 
            className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-gold"
            variants={itemVariants}
          >
            THE GUJARAT STATE ASSISTANT PUBLIC PROSECUTORS ASSOCIATION 
          </motion.h1>
          {/* <motion.p 
            className="text-xl md:text-3xl mb-8 font-light"
            variants={itemVariants}
          >
            Committed to Social Service and Professional Excellence
          </motion.p> */}
          <motion.p 
            className="text-lg mb-10 max-w-3xl mx-auto leading-relaxed"
            variants={itemVariants}
          >
Justice with Integrity represents our unwavering commitment to uphold the rule of law with honesty, impartiality, and ethical responsibility.
We believe that true justice is achieved not merely through convictions, but through fair prosecution, transparency in process, and respect for constitutional values.          
</motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            variants={itemVariants}
          >
            <motion.a 
              href="/about" 
              className="bg-gold text-deep-black px-8 py-4 rounded-full font-semibold hover:bg-gold-dark transition-all duration-300 shadow-lg transform hover:scale-105"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Learn More About Us
            </motion.a>
            <motion.a 
              href="/contact" 
              className="border-2 border-gold text-gold px-8 py-4 rounded-full font-semibold hover:bg-gold hover:text-deep-black transition-all duration-300 shadow-lg transform hover:scale-105"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact Us
            </motion.a>
          </motion.div>
        </div>
      </motion.section>

      {/* About Section */}
      <motion.section 
        className="py-20 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={itemVariants}>
              <motion.h2 
                className="text-4xl font-bold mb-6 text-deep-black"
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                About Our Association
              </motion.h2>
              <motion.p 
                className="text-xl mb-8 text-gray-600 leading-relaxed"
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
Founded with a steadfast commitment to justice, integrity, and professional excellence, The Gujarat State Assistant Public Prosecutors Association has served as a strong and united voice for Assistant Public Prosecutors for over two decades.              </motion.p>
              
              <motion.a 
                href="/about" 
                className="bg-gold text-deep-black px-6 py-3 rounded-full font-semibold hover:bg-gold-dark transition-all duration-300"
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                Read Our Full Story
              </motion.a>
            </motion.div>
            <motion.div 
              className="relative"
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <img 
                src="\ABOUT\THE_GUJRAT_STATE_AAP_ASSOCIATION.jpeg" 
                alt="Association Meeting" 
                className="rounded-2xl shadow-xl w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Gallery Slider Section */}
      <motion.section 
        className="py-20 bg-light-grey"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <div className="container mx-auto px-4">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-deep-black">
              Photo Gallery
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore our collection of memorable moments and events from the association
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} className="max-w-6xl mx-auto">
            <GallerySlider />
          </motion.div>

          <motion.div 
            variants={itemVariants}
            className="text-center mt-12"
          >
            <motion.a 
              href="/gallery" 
              className="bg-gold text-deep-black px-8 py-4 rounded-full font-semibold hover:bg-gold-dark transition-all duration-300 inline-block"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Full Gallery
            </motion.a>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Home;